/**
 * useChatNotes — Iter 156.22 (Phase A, Yash).
 * Extracted from `ChatsPage.jsx`. Owns the internal-notes panel state for
 * the currently-selected lead: the notes list, the draft input, and the
 * add/delete handlers.
 *
 * Caller passes the selected lead so the hook knows which lead-id to talk
 * to, and the hook returns everything the `<NotesSection>` component
 * already expected as props — no JSX changes needed.
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { notesApi } from "../lib/api";

export default function useChatNotes(selectedLead) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");

  const fetchNotes = useCallback(async (leadId) => {
    try {
      const res = await notesApi.get(leadId);
      setNotes(res.data.notes || []);
    } catch {
      /* leave previous list — better UX than blanking on transient fail */
    }
  }, []);

  const addNote = useCallback(async () => {
    if (!newNote.trim() || !selectedLead) return;
    try {
      await notesApi.add(selectedLead.id, newNote.trim());
      setNewNote("");
      await fetchNotes(selectedLead.id);
      toast.success("Note added");
    } catch {
      toast.error("Failed to add note");
    }
  }, [newNote, selectedLead, fetchNotes]);

  const deleteNote = useCallback(async (noteId) => {
    if (!selectedLead) return;
    try {
      await notesApi.delete(noteId);
      await fetchNotes(selectedLead.id);
      toast.success("Note deleted");
    } catch {
      toast.error("Failed");
    }
  }, [selectedLead, fetchNotes]);

  return { notes, setNotes, newNote, setNewNote, fetchNotes, addNote, deleteNote };
}
